import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User } from "@/api/entities";
import { saveToR2 } from "@/api/r2";

const CATEGORIES = ["UI/UX", "Audio/Video", "Notifiche", "Prestazioni", "Dati/Progressi", "Altro"];
const SEVERITIES = ["bassa", "media", "alta", "bloccante"];

export default function BugReportModal({ open, onClose }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [category, setCategory] = useState("Altro");
  const [severity, setSeverity] = useState("media");
  const [contactEmail, setContactEmail] = useState("");
  const [attachUrl, setAttachUrl] = useState("");
  const [includeTech, setIncludeTech] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const me = await User.me();
        if (me?.email) setContactEmail(me.email);
      } catch {}
    })();
  }, [open]);

  if (!open) return null;

  function collectTech() {
    if (!includeTech) return null;
    const tech = {
      url: window.location.href,
      ua: navigator.userAgent,
      lang: navigator.language,
      sw: !!navigator.serviceWorker?.controller,
      notif: typeof Notification !== "undefined" ? Notification.permission : "n/a",
      screen: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
      ts: new Date().toISOString(),
    };
    return tech;
  }

  async function submit() {
    setError("");
    if (!title.trim() || !desc.trim()) {
      setError("Titolo e descrizione sono obbligatori.");
      return;
    }
    setSubmitting(true);
    const payload = {
      title: title.trim(),
      description: desc.trim(),
      category,
      severity,
      contact_email: contactEmail.trim() || null,
      attach_url: attachUrl.trim() || null,
      tech: collectTech(),
    };

    try {
      // Prova la function che salva in R2 ed eventualmente invia mail
      const res = await fetch("/api/bug-report", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);

      // chiudi e pulisci
      onClose?.();
      setTitle("");
      setDesc("");
      setAttachUrl("");
    } catch (e) {
      // Fallback: log locale in R2 con outbox
      await saveToR2("bug_report", { data: payload, meta: { source: "bug_modal", version: "v1" } });
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-2xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold">Segnala un bug</h3>
            <button onClick={onClose} className="text-sm text-gray-500 hover:underline">Chiudi</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Titolo *</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Audio non parte nella lezione 1" />
            </div>
            <div>
              <Label>Email (per contatto)</Label>
              <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} inputMode="email" />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Severità</Label>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Descrizione *</Label>
            <textarea
              className="w-full min-h-[120px] rounded-md border p-2 text-sm"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Cosa stavi facendo? Cosa ti aspettavi? Cosa è successo invece?"
            />
          </div>

          <div>
            <Label>Screenshot o link (opzionale)</Label>
            <Input value={attachUrl} onChange={(e) => setAttachUrl(e.target.value)} placeholder="https://… (Drive/Imgur ecc.)" />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={includeTech} onChange={(e) => setIncludeTech(e.target.checked)} />
            Includi informazioni tecniche (browser, versione, permessi)
          </label>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={submit} disabled={submitting}>{submitting ? "Invio..." : "Invia segnalazione"}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
